import { useAuth0 } from "@auth0/auth0-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import PropTypes from "prop-types";
import { useState, useEffect } from "react";

import "@/components/membership/Dashboard/Dashboard.css";
import API_Calls_Dashboard from "@/api/calls/API_Calls_Dashboard";
import { CustomCatalistExportModal } from "@/components/membership/Dashboard/DashboardComponents/CustomCatalistExportModal";
import { getDashboardIcon } from "@/components/membership/Dashboard/DashboardUtils/dashboardIcons";
import Notifications from "@/components/Notifications/Notifications";
import SiteIcons from "@/components/SiteIcons/SiteIcons";
import { parseAdaPath, formatTimestamp, formatLargeNumber } from "@/utils/util";

function CatalistModal({ row }) {
  const { user } = useAuth0();
  const [open, setOpen] = useState(false);
  const [showCatalistData, setShowCatalistData] = useState(false);
  const [tableRows, setTableRows] = useState([]);
  const [file, setFile] = useState(row);
  const [notification, setNotification] = useState({});
  const { downloadCatalistFile, setCatalistFieldPreferences } =
    API_Calls_Dashboard();
  const [downloadButtonIsDisabled, setDownloadButtonIsDisabled] =
    useState(false);
  const [customFields, setCustomFields] = useState({
    fields: [],
    format: "csv",
  });

  useEffect(() => {
    setFile(row);
    setShowCatalistData(row.catalist?.completed);
  }, [row]);

  useEffect(() => {
    if (file.catalist) {
      createTable();
    }
    // createTable is an inline function recreated every render; depending
    // on it would cause infinite re-runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const handleClickOpen = () => {
    fetchPreferences();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCustomFields = (params) => {
    setCustomFields({
      format: params.format,
      fields: params.fields,
    });
  };

  const fetchPreferences = async () => {
    // TODO cache settings in local storage
    const res = await setCatalistFieldPreferences(user.org_name);
    if (res.data?.preferred_catalist_fields) {
      const { parameterSettings: params, ...catalistFields } =
        res.data.preferred_catalist_fields;

      // convert preferred fields object into array of field strings
      const fields = [];
      Object.values(catalistFields).forEach((fieldObject) => {
        Object.entries(fieldObject).forEach(([fieldName, isPreferred]) => {
          if (isPreferred) {
            fields.push(fieldName);
          }
        });
      });
      setCustomFields({
        fields,
        format: params.format,
      });
    }
  };

  const handleCatalistDownload = async () => {
    setNotification({
      message: "Download started",
      severity: "info",
      duration: 3000,
    });
    setDownloadButtonIsDisabled(true);
    const affiliate = parseAdaPath(file.ada_file).affiliate;
    const timestamp = parseAdaPath(file.ada_file).process_date;

    const response = await downloadCatalistFile(
      affiliate,
      timestamp,
      customFields
    );
    if (response.error) {
      setNotification({
        message: "Unable to download at this time",
        severity: "error",
        duration: 7000,
      });
      setDownloadButtonIsDisabled(false);
    } else {
      setDownloadButtonIsDisabled(false);
    }
    setDownloadButtonIsDisabled(false);
  };

  const calculateDWIDSaturationChange = (saturation, previous_saturation) => {
    if (!saturation || !previous_saturation) {
      return dataNotAvailable();
    } else {
      return `${((saturation - previous_saturation) * 100).toFixed(2)}%`;
    }
  };

  const calculateMissingDWIDRows = (dwidSaturation, catalistRows) => {
    if (!dwidSaturation || !catalistRows) {
      return dataNotAvailable();
    } else {
      const result = Math.floor((1 - dwidSaturation) * catalistRows);
      return formatLargeNumber(result);
    }
  };

  const dataNotAvailable = () => {
    return "Unavailable";
  };

  const createTable = () => {
    const rows = [
      {
        fieldName: "Job Id",
        value: file.catalist.job_id ? file.catalist.job_id : dataNotAvailable(),
      },
      {
        fieldName: "Processed by Catalist",
        value: file.catalist.completed
          ? formatTimestamp(file.catalist.completed)
          : dataNotAvailable(),
      },
      {
        fieldName: "DWID Saturation",
        value: file.catalist.dwid_saturation
          ? `${(file.catalist.dwid_saturation * 100).toFixed(2)}%`
          : dataNotAvailable(),
      },

      {
        fieldName: "Date of comparison file",
        value: file.catalist.previous_processed_date
          ? formatTimestamp(file.catalist.previous_processed_date)
          : dataNotAvailable(),
      },
      {
        fieldName: "Change in DWID saturation from last file",
        value: calculateDWIDSaturationChange(
          file.catalist.dwid_saturation,
          file.catalist.previous_dwid_saturation
        ),
      },
      {
        fieldName: "Number of records with missing DWID",
        value: calculateMissingDWIDRows(
          file.catalist.dwid_saturation,
          file.catalist.catalist_rows
        ),
      },
    ];

    // Don't show catalist comparison data for catalist only files
    if (row.use === "catalist-only") {
      setTableRows(rows.filter((_, index) => index !== 3 && index !== 4));
    } else {
      setTableRows(rows);
    }
  };

  return (
    <>
      {getDashboardIcon(file, "catalist", handleClickOpen)}

      <Dialog onClose={handleClose} open={open} data-testid="catalist-modal">
        <>
          <DialogTitle className="seiu-modal-header">
            Catalist File Summary
          </DialogTitle>
          <DialogContent dividers>
            {showCatalistData ? (
              <>
                <TableContainer>
                  <Table sx={{ minWidth: 550 }} aria-label="simple table">
                    <TableBody>
                      {tableRows.map((row) => (
                        <TableRow
                          key={row.fieldName}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell
                            component="th"
                            scope="row"
                            sx={{ color: "#000000a6" }}
                          >
                            {row.fieldName}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 400 }}>
                            {row.value}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ) : (
              <Box>
                <Typography sx={{ fontStyle: "italic" }}>
                  No summary available for this file
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions
            sx={{ display: "flex", justifyContent: "space-between" }}
          >
            <Box>
              <Button
                // Focus the safe (Close) button on open
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                disabled={downloadButtonIsDisabled}
                className="siteBlueOutlined"
                onClick={handleClose}
              >
                Close
              </Button>
            </Box>
            <Box>
              <CustomCatalistExportModal
                handleCustomFields={handleCustomFields}
              />
              <Button className="siteBlue" onClick={handleCatalistDownload}>
                <SiteIcons
                  iconstring="download"
                  color="white"
                  sx={{ marginRight: "10px" }}
                />
                Download Catalist Data File
              </Button>
            </Box>
          </DialogActions>
        </>
      </Dialog>

      {!!Object.keys(notification).length && (
        <Notifications
          notification={notification}
          setNotification={setNotification}
        />
      )}
    </>
  );
}

CatalistModal.propTypes = {
  row: PropTypes.object,
};

export default CatalistModal;
